import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { Spin, Typography, Row, Col, Carousel, Tag, Button, Image } from "antd";
import styled from "styled-components";
import HomeLayout from "../layouts/HomeLayout";
import { HeaderTitle, HeaderSubtitle } from "../styles/sharedStyles";
import { colors } from "../utils/theme";
import { device } from "../utils/breakpoints";
import StyleElement from "../components/StyleElement";
import { isValidHttpUrl } from "../functions/Browser";
import { PROFESSIONS } from "../constants/ProfessionConstants";
import { SKILLS } from "../constants/SkillsConstants";
import MetaTags from "../components/MetaTags";
import { InstagramOutlined, TikTokOutlined } from "@ant-design/icons";

const ProfileDetail = () => {
  const { publicName } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  const users = useSelector((state) => state.firestoreReducer.ordered.users);
  const isLoading = useSelector(
    (state) => state.firestoreReducer.status.requesting.users,
  );

  const [member, setMember] = useState(null);
  const [profession, setProfession] = useState(null);

  useEffect(() => {
    if (users && users.length > 0) {
      const foundMember = users.find(
        (user) => user.urlSlug === publicName && user.public === true,
      );

      if (foundMember) {
        setMember(foundMember);

        // Find profession display name
        const foundProfession = PROFESSIONS.find(
          (prof) => prof.id === foundMember.profession,
        );
        if (foundProfession) {
          setProfession(foundProfession[lang]);
        }
      } else {
        // If member not found or not public, redirect to community page
        navigate("/community");
      }
    }
  }, [users, publicName, lang, navigate]);

  if (isLoading) {
    return (
      <HomeLayout>
        <LoadingWrapper>
          <Spin size="large" />
        </LoadingWrapper>
      </HomeLayout>
    );
  }

  if (!member) {
    return (
      <HomeLayout>
        <LoadingWrapper>
          <HeaderTitle>{t("member_not_found")}</HeaderTitle>
          <Button type="primary" onClick={() => navigate("/community")}>
            {t("back_to_community")}
          </Button>
        </LoadingWrapper>
      </HomeLayout>
    );
  }

  // Get display name (full name or stage name if available)
  const displayName =
    member.publicName ||
    `${member.firstName || ""} ${member.lastName || ""}`.trim();

  return (
    <HomeLayout>
      <MetaTags
        title={`${displayName} - ${t("meta.member_profile_title")}`}
        description={member.about || t("meta.member_profile_description")}
        imageUrl={member.avatar || "/assets/images/crew/ow-crew-1.webp"}
        path={`/community/${publicName}`}
      />

      <ProfileWrapper>
        <StyleElementContainer>
          <StyleElement
            src="/assets/images/illustrations/illustratie-06.svg"
            alt=""
            left
            bottom={-60}
          />
        </StyleElementContainer>

        <ProfileHeader>
          <BackButton onClick={() => navigate("/community")}>
            <i className="ti-arrow-left"></i> {t("back_to_community")}
          </BackButton>

          <ProfileContent>
            <Row gutter={[24, 24]}>
              <Col xs={24} md={8}>
                <AvatarContainer>
                  <Avatar
                    src={member.avatar || "/assets/images/user/placeholder.png"}
                    alt={displayName}
                  />
                </AvatarContainer>
              </Col>

              <Col xs={24} md={16}>
                <ProfileInfo>
                  <HeaderTitle>{displayName}</HeaderTitle>
                  {profession && <Profession>{profession}</Profession>}

                  {member.skills && member.skills.length > 0 && (
                    <SkillsContainer>
                      {member.skills.map((skill, index) => {
                        const skillItem = SKILLS.find((s) => s.id === skill);
                        return (
                          <Tag key={index} color={colors.secondary}>
                            {skillItem ? skillItem[lang] : skill}
                          </Tag>
                        );
                      })}
                    </SkillsContainer>
                  )}

                  {member.richBio && (
                    <AboutSection>
                      <div className={"bio"}>BIO</div>
                      <p>{member.richBio}</p>
                    </AboutSection>
                  )}

                  <SocialLinks>
                    {member.externalLink && (
                      <SocialLink
                        href={
                          isValidHttpUrl(member.externalLink)
                            ? member.externalLink
                            : `https://${member.externalLink}`
                        }
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <i className="ti-world"></i>{" "}
                        {member.externalLinkText || t("website")}
                      </SocialLink>
                    )}

                    {member.linkedinLink && (
                      <SocialLink
                        href={
                          isValidHttpUrl(member.linkedinLink)
                            ? member.linkedinLink
                            : `https://${member.linkedinLink}`
                        }
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <i className="ti-linkedin"></i>
                      </SocialLink>
                    )}

                    {member.instagramLink && (
                      <SocialLink
                        href={
                          isValidHttpUrl(member.instagramLink)
                            ? member.instagramLink
                            : `https://${member.instagramLink}`
                        }
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <InstagramOutlined />
                      </SocialLink>
                    )}

                    {member.tiktokLink && (
                      <SocialLink
                        href={
                          isValidHttpUrl(member.tiktokLink)
                            ? member.tiktokLink
                            : `https://${member.tiktokLink}`
                        }
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <TikTokOutlined />
                      </SocialLink>
                    )}
                  </SocialLinks>
                </ProfileInfo>
              </Col>
            </Row>
          </ProfileContent>
        </ProfileHeader>

        {member.profileImages && member.profileImages.length > 0 && (
          <GallerySection>
            <SectionTitle>{t("gallery")}</SectionTitle>
            <ImageGrid>
              <Image.PreviewGroup>
                <Row gutter={[16, 16]}>
                  {member.profileImages.map((image, index) => (
                    <Col xs={12} sm={8} md={6} key={index}>
                      <Image
                        src={image}
                        alt={`${displayName} - ${index + 1}`}
                        preview={{
                          mask: (
                            <div className="image-hover-overlay">
                              <i className="ti-zoom-in"></i>
                            </div>
                          ),
                          maskClassName: "custom-mask",
                        }}
                      />
                    </Col>
                  ))}
                </Row>
              </Image.PreviewGroup>
            </ImageGrid>
          </GallerySection>
        )}

        {(member.showEmail || member.showPhone) && (
          <ContactSection>
            <SectionTitle>{t("contact")}</SectionTitle>
            <ContactInfo>
              {member.showEmail && member.email && (
                <ContactItem>
                  <i className="ti-email"></i> {member.email}
                </ContactItem>
              )}
              {member.showPhone && member.phone && (
                <ContactItem>
                  <i className="ti-mobile"></i> {member.phone}
                </ContactItem>
              )}
            </ContactInfo>
          </ContactSection>
        )}
      </ProfileWrapper>
    </HomeLayout>
  );
};

const LoadingWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  min-height: 400px;
  gap: 2rem;
`;

const StyleElementContainer = styled.div`
  position: relative;
  overflow: hidden;
  width: 100%;
  height: 100%;
`;

const ProfileWrapper = styled.div`
  padding: 2rem 1rem;
  max-width: 1200px;
  margin: 0 auto;
  position: relative;

  @media ${device.md} {
    padding: 3rem 2rem;
  }
`;

const ProfileHeader = styled.div`
  position: relative;
  margin-bottom: 2rem;
`;

const BackButton = styled.button`
  background: none;
  border: none;
  padding: 0.5rem 0;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: ${colors.secondary};
  font-weight: 500;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;

const ProfileContent = styled.div`
  padding: 2rem;
  background-color: rgba(255, 255, 255, 0.7);
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
`;

const AvatarContainer = styled.div`
  display: flex;
  justify-content: center;

  @media ${device.md} {
    justify-content: flex-start;
  }
`;

const Avatar = styled.img`
  width: 200px;
  height: 200px;
  border-radius: 50%;
  object-fit: cover;
`;

const ProfileInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const Profession = styled(HeaderSubtitle)`
  margin-top: -0.5rem;
  font-size: 1rem;
  color: ${colors.secondary};
`;

const SkillsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 0.5rem;
`;

const AboutSection = styled.div`
  border-top: 1px solid #ddd;
  border-bottom: 1px solid #ddd;
  padding: 2rem 0;
  margin: 2rem 0;

  .bio {
    font-size: 1.3rem;
    font-weight: 600;
    margin-bottom: 0.5rem;
    color: ${colors.header};
  }

  p {
    white-space: pre-line;
    line-height: 1.5;
  }
`;

const SocialLinks = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  margin-top: 1rem;
`;

const SocialLink = styled.a`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: ${colors.header};
  text-decoration: none;
  border-radius: 4px;
  transition: all 0.2s ease;
  margin: 0 0.5rem;

  &:hover {
    color: ${colors.secondary};
  }

  i {
    font-size: 1.2rem;
  }

  svg {
    font-size: 1.2rem;
  }
`;

const GallerySection = styled.div`
  margin: 3rem 0;
  padding: 2rem;
  background-color: rgba(255, 255, 255, 0.7);
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
`;

const SectionTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 700;
  margin-bottom: 1.5rem;
  color: ${colors.header};
`;

const ImageGrid = styled.div`
  .ant-image {
    width: 100%;
    height: 100%;
    display: block;
    border-radius: 8px;
    overflow: hidden;
    cursor: pointer;
    transition: transform 0.3s ease;

    &:hover {
      transform: scale(1.03);
    }
  }

  .ant-image-img {
    width: 100%;
    height: 180px;
    object-fit: cover;
    border-radius: 8px;
  }

  .image-hover-overlay {
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.5rem;
    color: white;
  }

  .custom-mask {
    background-color: rgba(0, 0, 0, 0.5);
    border-radius: 8px;
  }

  @media ${device.sm} {
    .ant-image-img {
      height: 160px;
    }
  }

  @media ${device.md} {
    .ant-image-img {
      height: 180px;
    }
  }
`;

const ContactSection = styled.div`
  padding: 2rem;
  background-color: rgba(255, 255, 255, 0.7);
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  margin-bottom: 3rem;
`;

const ContactInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const ContactItem = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  font-size: 1rem;

  i {
    color: ${colors.secondary};
    font-size: 1.2rem;
  }
`;

export default ProfileDetail;
